import { useState } from 'react';
import faqimg from '../assets/images/faq_img.png';
import PageHeader from '@/components/PageHeader';
interface AccordionItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

const AccordionItem = ({
  question,
  answer,
  isOpen,
  onToggle,
}: AccordionItemProps) => {
  return (
    <div className="border rounded-md overflow-hidden shadow-sm bg-white">
      <button
        onClick={onToggle}
        className={`w-full text-left px-4 py-4 flex justify-between items-center hover:bg-gray-50 transition duration-150 ${isOpen ? 'bg-blue-50' : ''
          }`}
      >
        <span className={`flex-1 text-left ${isOpen ? 'font-semibold' : ''}`}>
          {question}
        </span>
        <span className="text-xl text-blue-600 font-bold">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      {isOpen && <div className="px-6 pb-5 text-sm text-gray-700">{answer}</div>}
    </div>
  );
};


interface Faq {
  question: string;
  answer: React.ReactNode;
}

function Faqs() {
  const [openIndex, setOpenIndex] = useState<number | null>(0); // Default open first

  const faqs: Faq[] = [
    {
      question:
        'What are the latest updates in ICH guidelines relevant to excipients and formulation development?',
      answer: (
        <div className="space-y-2 text-[#05326D]">
          <p>Recent ICH guidelines impacting excipient and formulation development include:</p>
          <p>
            <strong>ICH Q3D(R2)</strong>: Focuses on elemental impurities and sets PDE (Permitted Daily Exposure) limits for excipients.
          </p>
          <p>
            <strong>ICH Q14</strong>: Introduces a structured approach to analytical procedure development.
          </p>
          <p>
            <strong>ICH Q12</strong>: Supports lifecycle management of pharmaceutical products, including excipient changes post-approval. These guidelines promote risk-based decision-making and harmonized global standards.
          </p>
        </div>
      ),
    },
    {
      question: "How does the FDA’s Inactive Ingredient Database (IID) impact excipient selection for new drug formulations?",
      answer: (
        <div className="text-[#05326D]">
          <p>The IID helps in determining the safe use and regulatory acceptance of excipients in specific dosage forms, aiding in risk assessment.</p>
        </div>
      ),
    },
    {
      question: 'What is the role of Excipient DMFs in global regulatory submissions beyond the US?',
      answer: (
        <div className="text-[#05326D]">
          <p>Excipient DMFs provide proprietary manufacturing information and are useful for regulatory review in multiple regions, not just the US.</p>
        </div>
      ),
    },
    {
      question: 'How are nitrosamine impurities assessed and controlled in excipients?',
      answer: (
        <div className="text-[#05326D]">
          <p>Risk assessments, analytical testing, and process optimization are key approaches for managing nitrosamine levels.</p>
        </div>
      ),
    },
    {
      question: 'What are the regulatory considerations for using novel excipients in paediatric or orphan drug formulations?',
      answer: (
        <div className="text-[#05326D]">
          <p>Justification of safety, limited exposure data, and risk-benefit assessments are critical when using novel excipients in these populations.</p>
        </div>
      ),
    },
    {
      question: 'How do regulators evaluate excipient safety in complex dosage forms like inhalation, ophthalmic, or transdermal products?',
      answer: (
        <div className="text-[#05326D]">
          <p>Evaluation is based on toxicological profiles, local vs systemic exposure, and dosage route-specific risks.</p>
        </div>
      ),
    },
    {
      question: 'What are the expectations for data integrity in analytical testing of excipients?',
      answer: (
        <div className="text-[#05326D]">
          <p>Maintaining audit trails, ensuring accurate records, and validation of software systems are key expectations.</p>
        </div>
      ),
    },
    {
      question: 'How do regulatory agencies harmonize excipient standards across pharmacopeias (USP, EP, JP)?',
      answer: (
        <div className="text-[#05326D]">
          <p>Through IPEC guidelines, harmonization projects, and collaborative meetings between pharmacopeial bodies.</p>
        </div>
      ),
    },
    {
      question: 'What is the impact of Brexit on excipient registration and compliance in the UK?',
      answer: (
        <div className="text-[#05326D]">
          <p>Brexit created regulatory divergence; separate registration and documentation are now often required in the UK and EU.</p>
        </div>
      ),
    },
    {
      question: 'Can excipients be patented, and how does intellectual property affect regulatory filings?',
      answer: (
        <div className="text-[#05326D]">
          <p>Yes, excipients can be patented. IP considerations may affect data sharing, regulatory strategy, and market exclusivity.</p>
        </div>
      ),
    },
  ];

  const toggleAccordion = (index: number) => {
    setOpenIndex(prev => (prev === index ? null : index));
  };

  return (
    <div className="font-sans text-gray-800">
      {/* Top Contact Bar */}
      <PageHeader title='FAQ’s' imageUrl={faqimg} breadcrumbs={['Home', 'FAQ’s']}/>

      {/* Accordion Section */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="text-xl font-bold text-[#05326D] flex items-center gap-2 mb-12">
            <span className="inline-block w-[6px] h-5 rounded-tr-sm rounded-br-sm bg-[#B5AB28]"></span>
            FAQ’s
          </h2>

        <div className="flex flex-col md:flex-row items-start gap-8">
          <div className="order-1 md:order-none md:w-2/3 text-sm text-[#05326D] space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onToggle={() => toggleAccordion(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Faqs;
